import { Filesystem, Directory } from '@capacitor/filesystem';

/**
 * Image File Manager Utility
 * 
 * Manages local file system storage for images as a backup mechanism.
 * Images are stored in the Documents directory under /novacheck/ folder.
 * This provides a failsafe in case S3 upload fails or database gets corrupted.
 */
class ImageFileManager {
    constructor() {
        this.baseDir = 'novacheck';
    }

    /**
     * Save image to local file system
     * @param {string} imageUuid - The UUID of the image
     * @param {string} base64Data - The base64 encoded image data
     * @returns {Promise<string>} - The file path where image was saved
     */
    async saveImageToFile(imageUuid, base64Data) {
        try {
            // Remove data URL prefix if present
            const cleanBase64 = base64Data.replace(/^data:image\/\w+;base64,/, '');
            
            const path = `${this.baseDir}/${imageUuid}.jpg`;
            
            await Filesystem.writeFile({
                path: path,
                data: cleanBase64,
                directory: Directory.Documents,
                recursive: true // Create directory if it doesn't exist
            });
            
            console.log(`Image saved to local file: ${path}`);
            return path;
        } catch (error) {
            console.error('Error saving image to file:', error);
            throw error;
        }
    }

    /**
     * Read image from local file system
     * @param {string} imageUuid - The UUID of the image to read
     * @returns {Promise<string|null>} - The base64 encoded image data or null if not found
     */
    async readImageFromFile(imageUuid) {
        try {
            const path = `${this.baseDir}/${imageUuid}.jpg`;
            
            const result = await Filesystem.readFile({
                path: path,
                directory: Directory.Documents
            });
            
            // Return with data URL prefix
            return `data:image/jpeg;base64,${result.data}`;
        } catch (error) {
            if (error.message && error.message.includes('File does not exist')) {
                console.log(`Image file not found: ${imageUuid}`);
                return null;
            }
            console.error('Error reading image from file:', error);
            throw error;
        }
    }

    /**
     * Delete image file from local storage
     * @param {string} imageUuid - The UUID of the image to delete
     * @returns {Promise<boolean>} - True if deleted, false if file didn't exist
     */
    async deleteImageFile(imageUuid) {
        try {
            const path = `${this.baseDir}/${imageUuid}.jpg`;
            
            await Filesystem.deleteFile({
                path: path,
                directory: Directory.Documents
            });
            
            console.log(`Image file deleted: ${path}`);
            return true;
        } catch (error) {
            if (error.message && error.message.includes('File does not exist')) {
                return false;
            }
            console.error('Error deleting image file:', error);
            throw error;
        }
    }

    /**
     * Check if image file exists
     * @param {string} imageUuid - The UUID of the image to check
     * @returns {Promise<boolean>} - True if file exists
     */
    async imageFileExists(imageUuid) {
        try {
            const path = `${this.baseDir}/${imageUuid}.jpg`;
            
            await Filesystem.stat({
                path: path,
                directory: Directory.Documents
            });
            
            return true;
        } catch (error) {
            return false;
        }
    }

    /**
     * List all locally stored images
     * @returns {Promise<Array>} - Array of image file names
     */
    async listLocalImages() {
        try {
            const result = await Filesystem.readdir({
                path: this.baseDir,
                directory: Directory.Documents
            });
            
            // Filter for image files only
            return result.files.filter(file => 
                file.name.endsWith('.jpg') || 
                file.name.endsWith('.jpeg') || 
                file.name.endsWith('.png')
            );
        } catch (error) {
            if (error.message && error.message.includes('Directory does not exist')) {
                return [];
            }
            console.error('Error listing local images:', error);
            throw error;
        }
    }

    /**
     * Save multiple images to local storage
     * @param {Array} images - Array of image objects with uuid and base64 properties
     * @returns {Promise<Array>} - Array of save results
     */
    async saveMultipleImages(images) {
        const results = [];
        
        for (const image of images) {
            try {
                if (image.base64 && image.uuid) {
                    const path = await this.saveImageToFile(image.uuid, image.base64);
                    results.push({ uuid: image.uuid, path, success: true });
                }
            } catch (error) {
                console.error(`Failed to save image ${image.uuid}:`, error);
                results.push({ uuid: image.uuid, error: error.message, success: false });
            }
        }
        
        return results;
    }

    /**
     * Clean up old successfully uploaded images
     * @param {Array} successfulUuids - Array of UUIDs that have been successfully uploaded
     * @returns {Promise<number>} - Number of files deleted
     */
    async cleanupSuccessfulUploads(successfulUuids) {
        let deletedCount = 0;
        
        for (const uuid of successfulUuids) {
            try {
                const deleted = await this.deleteImageFile(uuid);
                if (deleted) deletedCount++;
            } catch (error) {
                console.error(`Failed to delete image ${uuid}:`, error);
            }
        }
        
        return deletedCount;
    }

    /**
     * Get storage info for local images
     * @returns {Promise<Object>} - Storage information
     */
    async getStorageInfo() {
        try {
            const images = await this.listLocalImages();
            let totalSize = 0;
            
            for (const image of images) {
                try {
                    const stat = await Filesystem.stat({
                        path: `${this.baseDir}/${image.name}`,
                        directory: Directory.Documents
                    });
                    totalSize += stat.size || 0;
                } catch (error) {
                    console.error(`Error getting size for ${image.name}:`, error);
                }
            }
            
            return {
                count: images.length,
                totalSizeBytes: totalSize,
                totalSizeMB: (totalSize / (1024 * 1024)).toFixed(2)
            };
        } catch (error) {
            console.error('Error getting storage info:', error);
            return {
                count: 0,
                totalSizeBytes: 0,
                totalSizeMB: '0.00'
            };
        }
    }
}

// Export singleton instance
export default new ImageFileManager();